import type { Components, JSX as SGBSComponents } from '@sg-bootstrap/components';
import { SgPicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import { useCallbackRef } from 'hooks/useCallbackRef';
import { first } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { WithDOMEvents } from 'types/web-components';

export interface TagItem {
  readonly key: string;
  readonly label: string;
  readonly forced?: boolean;
  readonly selected?: boolean;
  readonly info?: boolean;
}

export function isTagItem(item: any): item is TagItem {
  const castedItem = item as TagItem;
  return !!castedItem.key && !!castedItem.label;
}

export type TagsPickerProps<T extends TagItem = TagItem> = {
  id: string;
  e2e?: string;

  singleSelect: boolean;
  noIcon?: boolean;
  maxDisplayedItems?: number;
  placeholder?: string;
  keepOrder?: boolean;
  noClear?: boolean;
  available: Readonly<readonly T[]>;
  selection?: readonly string[];
  className?: string;
  hasTooltip?: boolean;
  tabIndex?: number;

  onSelectionKeysChanged?: (values: readonly string[]) => void;
  onSelectionItemsChanged?: (values: readonly T[]) => void;
  onValuePasted?: (value: string) => void | Promise<void>;
  onInputChanged?: (params: { value: string; hasResults: boolean }) => void | Promise<void>;
  onInputSubmit?: (value: string) => void | Promise<void>;
  onRemoveItem?: (value: TagItem) => void;
  onRemoveAll?: () => void;
} & Partial<Components.SgPicker>;

function areItemsEqual<T extends TagItem>(
  previousItems: readonly T[] | undefined,
  nextItems: readonly T[] | undefined,
): boolean {
  if (previousItems === nextItems) {
    return true;
  }
  if (previousItems === undefined || nextItems === undefined || previousItems.length !== nextItems.length) {
    return false;
  }

  return previousItems.every((previousItem, index) => {
    const nextItem = nextItems[index];
    const previousKeys = Object.keys(previousItem);
    const nextKeys = Object.keys(nextItem);

    return (
      previousKeys.length === nextKeys.length &&
      previousKeys.every(key => Object.is(previousItem[key as keyof T], nextItem[key as keyof T]))
    );
  });
}

export function SgTagsPicker<T extends TagItem = TagItem>({
  id,
  available,
  onSelectionKeysChanged,
  selection,
  singleSelect,
  className,
  placeholder = 'Add a tag',
  keepOrder = false,
  noIcon = false,
  maxDisplayedItems = 20,
  noClear = false,
  hasTooltip,
  onValuePasted,
  onInputSubmit,
  onInputChanged,
  onSelectionItemsChanged,
  onRemoveItem,
  onRemoveAll,
  e2e,
  tabIndex,
  ...props
}: TagsPickerProps<T>) {
  const availableRef = useRef(available);
  const onSelectionKeysChangedRef = useRef(onSelectionKeysChanged);
  const onSelectionItemsChangedRef = useRef(onSelectionItemsChanged);
  const onInputChangedRef = useRef(onInputChanged);
  const onInputSubmitRef = useRef(onInputSubmit);
  const onValuePastedRef = useRef(onValuePasted);
  const onRemoveItemRef = useRef(onRemoveItem);
  const onRemoveAllRef = useRef(onRemoveAll);
  const previousItemsRef = useRef<readonly T[] | undefined>(undefined);

  availableRef.current = available;
  onSelectionKeysChangedRef.current = onSelectionKeysChanged;
  onSelectionItemsChangedRef.current = onSelectionItemsChanged;
  onInputChangedRef.current = onInputChanged;
  onInputSubmitRef.current = onInputSubmit;
  onValuePastedRef.current = onValuePasted;
  onRemoveItemRef.current = onRemoveItem;
  onRemoveAllRef.current = onRemoveAll;

  const items = useMemo(
    () =>
      available.map(item => ({
        ...item,
        selected: selection !== undefined ? selection.includes(item.key) : item.selected,
      })),
    [available, selection],
  );

  const mapItemsToSelection = useCallback((selectedItems: T[]) => {
    onSelectionKeysChangedRef.current?.(selectedItems.map(item => item.key));
    onSelectionItemsChangedRef.current?.(selectedItems);
  }, []);

  const handleSelectItem = useCallback(
    async (e: CustomEvent<{}>) => {
      const node: Components.SgPicker = e.target as any;
      const selected: T[] = (await node.getSelection()) ?? [];
      mapItemsToSelection(selected);
    },
    [mapItemsToSelection],
  );

  const handleUnselectItem = useCallback(
    async (e: CustomEvent<{ item: TagItem }>) => {
      if (onRemoveItemRef.current) {
        onRemoveItemRef.current(e.detail.item);
        return;
      } else {
        const node: Components.SgPicker = e.target as any;
        const selected: T[] = (await node.getSelection()) ?? [];
        mapItemsToSelection(selected);
      }
    },
    [mapItemsToSelection],
  );

  const handleInputChanged = useCallback((e: CustomEvent<string>) => {
    const value = e.detail;
    // This tricks lets us know wether typed text has results or not
    const hasMatch = availableRef.current.some(item => item.key.includes(value) || item.label.includes(value));
    onInputChangedRef.current?.({ value, hasResults: hasMatch });
  }, []);

  const handleClear = useCallback(
    async (e: CustomEvent<{}>) => {
      if (onRemoveAllRef.current) {
        onRemoveAllRef.current();
      } else {
        const node: Components.SgPicker = e.target as any;
        const selected: T[] = (await node.getSelection()) ?? [];
        mapItemsToSelection(selected);
      }
    },
    [mapItemsToSelection],
  );

  const handleInputSubmit = useCallback(async (e: CustomEvent<{}>) => {
    const node: Components.SgPicker = e.target as any;
    const value = (await node.getValue()) ?? '';
    onInputSubmitRef.current?.(value);
  }, []);

  const handleValuePasted = useCallback((e: CustomEvent<string>) => {
    onValuePastedRef.current?.(e.detail);
  }, []);

  const updateRef = useCallback(
    (node: Components.SgPicker & WithDOMEvents<Components.SgPicker>) => {
      node?.addEventListener('inputSubmit', handleInputSubmit);
      node?.addEventListener('inputChanged', handleInputChanged);
      node?.addEventListener('itemsSelected', handleSelectItem);
      node?.addEventListener('itemsUnselected', handleUnselectItem);
      node?.addEventListener('inputCleared', handleClear);
      node?.addEventListener('valuePasted', handleValuePasted);
    },
    [handleInputChanged, handleInputSubmit, handleSelectItem, handleUnselectItem, handleValuePasted, handleClear],
  );
  const cleanRef = useCallback(
    (node: WithDOMEvents<Components.SgPicker & SGBSComponents.SgPicker> | null) => {
      node?.removeEventListener('inputSubmit', handleInputSubmit);
      node?.removeEventListener('inputChanged', handleInputChanged);
      node?.removeEventListener('itemsSelected', handleSelectItem);
      node?.removeEventListener('itemsUnselected', handleUnselectItem);
      node?.removeEventListener('inputCleared', handleClear);
      node?.removeEventListener('valuePasted', handleValuePasted);
    },
    [handleClear, handleInputChanged, handleInputSubmit, handleSelectItem, handleUnselectItem, handleValuePasted],
  );
  const { ref, callback } = useCallbackRef<WithDOMEvents<Components.SgPicker & SGBSComponents.SgPicker> | null>(
    updateRef,
    cleanRef,
  );

  useEffect(() => {
    if (ref.current === null || areItemsEqual(previousItemsRef.current, items)) {
      return;
    }

    ref.current.setItems(items);
    previousItemsRef.current = items;
  }, [items, ref]);

  const prevSelectionKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    const key = (selection ?? []).join('\u0000');
    if (prevSelectionKey.current !== undefined && prevSelectionKey.current !== key) {
      ref.current?.setValue('');
    }
    prevSelectionKey.current = key;
  }, [ref, selection]);

  const selected = first(selection);
  const tooltip = useMemo(() => available.find(x => x.key === selected)?.label, [selected, available]) ?? '';

  return (
    <div className={className}>
      <SgPicker
        inputId={id}
        data-e2e={e2e ?? id}
        ref={callback}
        createTag={false}
        showClearButton={!noClear}
        itemSelection={singleSelect ? 'single' : 'multiple'}
        keepOrder={keepOrder}
        showIcon={!noIcon}
        placeholder={placeholder}
        maxDisplayedItems={maxDisplayedItems}
        title={hasTooltip ? tooltip : ''}
        tabIndex={tabIndex}
        {...props}
      />
    </div>
  );
}
